import { useLocalization } from '@lib/localization';
import {
  CloudUpload,
  Description,
  PreviewOutlined,
  TaskAltOutlined,
  UploadFileOutlined,
} from '@mui/icons-material';
import { Box, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import { type ReactNode, useCallback, useState } from 'react';
import {
  Dropzone,
  ExampleTree,
  OverlineLabel,
  StatusAlert,
  StepBadge,
  StepBadgeNumber,
  UploadIconBubble,
} from './styles/BulkUpload.styled';

interface BulkUpdateModalEmptyStateProps {
  scanning: boolean;
  error: string | null;
  onFilesPicked: (files: FileList) => void;
  onItemsDropped: (items: DataTransferItemList) => void;
}

const BulkUpdateModalEmptyState = ({ scanning, error, onFilesPicked, onItemsDropped }: BulkUpdateModalEmptyStateProps) => {
  const { t } = useLocalization();
  const [dragOver, setDragOver] = useState(false);

  const handleClickDropzone = useCallback(() => {
    // We can't use a plain <input> in JSX because we need the `webkitdirectory`
    // attribute which React doesn't pass through cleanly.
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    (input as HTMLInputElement & { webkitdirectory: boolean }).webkitdirectory = true;

    input.onchange = (event) => {
      const files = (event.target as HTMLInputElement).files;
      if (files) onFilesPicked(files);
    };

    input.click();
  }, [onFilesPicked]);

  const handleDrop = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(false);
    onItemsDropped(event.dataTransfer.items);
  }, [onItemsDropped]);

  if (scanning) {
    return (
      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
        }}
      >
        <CircularProgress size={56} sx={{ color: 'rgb(2, 122, 242)' }} />
        <Typography variant="h6" fontWeight={500}>{t('Project_Bulk_Scanning_Title')}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t('Project_Bulk_Scanning_Subtitle')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flexGrow: 1,
        maxWidth: 960,
        width: '100%',
        mx: 'auto',
        py: 4,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 4,
      }}
    >
      <Box sx={{ width: '100%' }}>
        <Dropzone
          dragOver={dragOver}
          onClick={handleClickDropzone}
          onDrop={handleDrop}
          onDragOver={(event) => { event.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
        >
          <UploadIconBubble>
            <CloudUpload sx={{ fontSize: 40, color: 'white' }} />
          </UploadIconBubble>
          <Typography variant="h5" fontWeight={600}>
            {t('Project_Bulk_Dropzone_Title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
            {t('Project_Bulk_Dropzone_Subtitle')}
          </Typography>
          <Stack direction="row" spacing={1} justifyContent="center">
            <Chip
              size="small"
              icon={<Description sx={{ fontSize: 16 }} />}
              label=".spproject"
              sx={{ backgroundColor: 'rgba(96, 165, 250, 0.12)', color: '#60a5fa', fontWeight: 500 }}
            />
          </Stack>
        </Dropzone>
      </Box>

      <Box
        sx={{
          width: '100%',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 3,
        }}
      >
        <Box>
          <OverlineLabel variant="overline" color="text.secondary">{t('Project_Bulk_HowItWorks')}</OverlineLabel>
          <Stack spacing={1.5} sx={{ mt: 1.5 }}>
            <HowItWorksStep
              number={1}
              icon={<UploadFileOutlined fontSize="small" />}
              title={t('Project_Bulk_Step1_Title')}
              description={t('Project_Bulk_Step1_Desc')}
            />
            <HowItWorksStep
              number={2}
              icon={<PreviewOutlined fontSize="small" />}
              title={t('Project_Bulk_Step2_Title')}
              description={t('Project_Bulk_Step2_Desc')}
            />
            <HowItWorksStep
              number={3}
              icon={<TaskAltOutlined fontSize="small" />}
              title={t('Project_Bulk_Step3_Title')}
              description={t('Project_Bulk_Step3_Desc')}
            />
          </Stack>
        </Box>

        <Box>
          <OverlineLabel variant="overline" color="text.secondary">{t('Project_Bulk_ExampleFolder')}</OverlineLabel>
          <ExampleTree sx={{ mt: 1.5, color: 'text.secondary' }}>
            <Box sx={{ color: '#facc15' }}>📁 my-projects/</Box>
            <Box sx={{ pl: 2, color: '#facc15' }}>📁 batch-1/</Box>
            <Box sx={{ pl: 4, color: '#60a5fa' }}>📄 engine.spproject</Box>
            <Box sx={{ pl: 4, color: '#60a5fa' }}>📄 chassis.spproject</Box>
            <Box sx={{ pl: 2, color: '#facc15' }}>📁 batch-2/</Box>
            <Box sx={{ pl: 4, color: '#60a5fa' }}>📄 motor.spproject</Box>
          </ExampleTree>
        </Box>
      </Box>

      {error && (
        <Box sx={{ width: '100%' }}>
          <StatusAlert severity="error">
            <Typography fontWeight={500}>{error}</Typography>
          </StatusAlert>
        </Box>
      )}
    </Box>
  );
};

interface HowItWorksStepProps {
  number: number;
  icon: ReactNode;
  title: string;
  description: string;
}

const HowItWorksStep = ({ number, icon, title, description }: HowItWorksStepProps) => (
  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
    <StepBadge>
      {icon}
      <StepBadgeNumber>{number}</StepBadgeNumber>
    </StepBadge>
    <Box>
      <Typography variant="body2" fontWeight={600}>{title}</Typography>
      <Typography variant="caption" color="text.secondary">{description}</Typography>
    </Box>
  </Box>
);

export default BulkUpdateModalEmptyState;
